import {
  Backdrop,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import Label from "../../../components/Label";
import { parseDate, parseDateTime } from "../../../utils/tableFunctions";
import { Clear } from "@mui/icons-material";
import HeaderHelpPage from "../../../components/HeaderHelpPage";
import { styles } from "../../../utils/styles";
import PendingDetailsComparison from "../../../components/PendingDetailsComparison";
import { getPendingBankAccountDetails } from "../../../services/services";

const BANK_ACCOUNT_FIELDS = [
  ["account_number", "Account Number"],
  ["account_name", "Account Name"],
  ["bank", "Bank"],
  ["bank_address", "Bank Address"],
  ["bank_country", "Bank Country"],
  ["bank_branch_code", "Bank Branch Code"],
  ["bic_swift_code", "BIC/SWIFT Code"],
  ["iban", "IBAN"],
  ["bank_transfer_fee", "Bank Transfer Fee"],
  ["beneficiary_name", "Beneficiary Name"],
  ["beneficiary_address", "Beneficiary Address"],
  ["beneficiary_bank_code", "Beneficiary Bank Code"],
  ["beneficiary_branch_code", "Beneficiary Branch Code"],
  ["intermediary_name", "Intermediary Name"],
  ["intermediary_bank", "Intermediary Bank"],
  ["intermediary_bank_code", "Intermediary Bank Code"],
  ["transfer_charge_pay_by", "Transfer Charge Pay By"],
  ["settlement_fee", "Settlement Fee"],
  ["is_active", "Active"],
].map(([key, label]) => ({ key, label }));

const BankAccountDetails = ({ detailsData, itemId, isViewHistory }) => {
  const [bankAccountDetails, setBankAccountDetails] = useState();
  const [loaded, setLoaded] = useState();
  const { id = itemId } = useParams();
  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  };
  const action = useQuery().get("action") || (detailsData && "true") || (isViewHistory && "true");
  const mobile = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  // Every maker-and-checker view: the pending dialog and the history dialog.
  const isCheckerView = Boolean(action);
  const isHistoryView = Boolean(isViewHistory) || (!detailsData && Boolean(action));

  useEffect(() => {
    if (!detailsData && id) {
      getPendingBankAccountDetails(id, setBankAccountDetails, setLoaded);
      return;
    }
    setBankAccountDetails(detailsData);
    setLoaded(true);
  }, [action, detailsData, id]);

  const handleBack = () => window.close();

  const InfoColumn = () => {
    return (
      <Grid item xs={6} md={6}>
        <Box
          sx={{
            height: "100%",
            p: 3,
            border: "1px solid #999",
            borderRadius: 2,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Account Number</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {bankAccountDetails.account_number}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Account Name</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {bankAccountDetails.account_name}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Bank</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {bankAccountDetails.bank}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Bank Address</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {bankAccountDetails.bank_address}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Bank Country</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {bankAccountDetails.bank_country}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">
                Bank Branch Code
              </Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {bankAccountDetails.bank_branch_code}
            </Grid>

            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">
                BIC/SWIFT Code
              </Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {bankAccountDetails.bic_swift_code}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">
                IBAN
              </Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {bankAccountDetails.iban}
            </Grid>

            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">
                Bank Transfer Fee
              </Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {bankAccountDetails.bank_transfer_fee}
            </Grid>

            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">
                Beneficiary Name
              </Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {bankAccountDetails.beneficiary_name}
            </Grid>

            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">
                Beneficiary Address
              </Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {bankAccountDetails.beneficiary_address}
            </Grid>

            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">
                Beneficiary Bank Code
              </Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {bankAccountDetails.beneficiary_bank_code}
            </Grid>

            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">
                Beneficiary Branch Code
              </Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {bankAccountDetails.beneficiary_branch_code}
            </Grid>

            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">
                Intermediary Name
              </Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {bankAccountDetails.intermediary_name}
            </Grid>

            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">
                Intermediary Bank
              </Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {bankAccountDetails.intermediary_bank}
            </Grid>

            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">
                Intermediary Bank Code
              </Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {bankAccountDetails.intermediary_bank_code}
            </Grid>

            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">
                Transfer Charge Pay By
              </Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {bankAccountDetails.transfer_charge_pay_by}
            </Grid>

            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">
                Settlement Fee
              </Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {bankAccountDetails.settlement_fee}
            </Grid>

            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">
                Active
              </Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              <Label color={bankAccountDetails.is_active ? "success" : "error"}>
                {bankAccountDetails.is_active ? "YES" : "NO"}
              </Label>
            </Grid>

          </Grid>
        </Box>
      </Grid>
    );
  }

  const DateColumn = () => {
    const requestedOnLabel = isHistoryView ? "Requested on" : "Created on";
    const requestedByLabel = isHistoryView ? "Requested by" : "Created by";
    const reviewedOnLabel = isHistoryView ? "Reviewed on" : "Approved on";
    const reviewedByLabel = isHistoryView ? "Reviewed by" : "Approved by";
    const formatTimestamp = isHistoryView ? parseDateTime : parseDate;

    return (
      <Grid item xs={6} md={6}>
        <Box
          sx={{
            height: "100%",
            p: 3,
            border: "1px solid #999",
            borderRadius: 2,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">
                {action === "true" ? "Approved" : "Active Now"}
              </Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {action === "true" ? (
                <Label
                  variant="ghost"
                  color={bankAccountDetails.is_approved ? "success" : "error"}
                >
                  {bankAccountDetails.is_approved ? "YES" : "NO"}
                </Label>
              ) : (
                <Label
                  variant="ghost"
                  color={bankAccountDetails.is_active ? "success" : "error"}
                >
                  {bankAccountDetails.is_active ? "YES" : "NO"}
                </Label>
              )}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{requestedOnLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {bankAccountDetails.created_at
                ? formatTimestamp(bankAccountDetails.created_at)
                : "N/A"}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{requestedByLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {bankAccountDetails.created_by
                ? bankAccountDetails.created_by.email
                : "N/A"}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{reviewedOnLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {bankAccountDetails.approved_at
                ? formatTimestamp(bankAccountDetails.approved_at)
                : "N/A"}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{reviewedByLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {bankAccountDetails.approved_by
                ? bankAccountDetails.approved_by.email
                : "N/A"}
            </Grid>
            {!action && (
              <>
                <Grid item xs={6} md={5}>
                  <Typography variant="subtitle1">Updated on</Typography>
                </Grid>
                <Grid item xs={6} md={7}>
                  {parseDate(bankAccountDetails.updated_at)}
                </Grid>
                <Grid item xs={6} md={5}>
                  <Typography variant="subtitle1">Updated by</Typography>
                </Grid>
                <Grid item xs={6} md={7}>
                  {bankAccountDetails.updated_by
                    ? bankAccountDetails.updated_by.email
                    : "N/A"}
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Grid>
    );
  }

  return (
    <Container>
      {!detailsData && !itemId && (
        <Stack
          direction="row"
          sx={{
            ...styles.flexBetween,
            mb: 2,
          }}
        >
          <HeaderHelpPage title="Bank Account Details" variant={mobile ? "h5" : "h3"} />

          <Button
            variant="outlined"
            onClick={handleBack}
            endIcon={<Clear />}
          >
            Back
          </Button>
        </Stack>
      )}
      {loaded && action === "true" && (
        <Typography variant="h6" sx={{ mb: 2 }}>
          Action: {bankAccountDetails.action && bankAccountDetails.action}
        </Typography>
      )}
      {loaded && Boolean(bankAccountDetails?.original_unavailable) && (
        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
          Before values are not available for this request because it was
          submitted before change logging was in place.
        </Typography>
      )}
      {loaded ? (
        <>
          <Card sx={{ p: 3 }}>
            <Grid
              container
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 2, sm: 2, md: 2 }}
              sx={{ mt: 5 }}
            >
            {isCheckerView && !bankAccountDetails.original_unavailable ? (
              <>
                <PendingDetailsComparison
                  data={bankAccountDetails}
                  original={bankAccountDetails.original}
                  fields={BANK_ACCOUNT_FIELDS}
                />
                <DateColumn />
              </>
            ) : (
              <>
                {action && <DateColumn />}
                <InfoColumn />
                {!action && <DateColumn />}
              </>
            )}
            </Grid>
            {bankAccountDetails.reject_message && (
              <Typography variant="subtitle1" sx={{ mt: 2, display: "flex" }}>
                Reason for rejection:
                <Typography sx={{ ml: 1 }}>
                  {" "}
                  {bankAccountDetails.reject_message}
                </Typography>
              </Typography>
            )}
            {bankAccountDetails.action_message && (
              <Typography variant="subtitle1" sx={{ mt: 2, display: "flex" }}>
                Action message:
                <Typography sx={{ ml: 1 }}>
                  {" "}
                  {bankAccountDetails.action_message}
                </Typography>
              </Typography>
            )}
          </Card>
        </>
      ) : (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
    </Container>
  );
};

export default BankAccountDetails;
